import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../state/auth';
import { useCart } from '../state/cart';
import { trackPurchase } from '../lib/pixel';
import { useT } from '../i18n';

export default function CheckoutSuccess() {
  const t = useT();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState('polling');
  const [orderId, setOrderId] = useState(null);
  const attempts = useRef(0);
  const autoLoginConsumed = useRef(false);
  const purchaseTracked = useRef(false);
  const [payFirst, setPayFirst] = useState(false);
  const { applyToken } = useAuth();
  const { refreshPendingOrders, removeGuestItemByShareToken, clearSlots } = useCart();

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }

    const poll = setInterval(async () => {
      try {
        const data = await api.get(`/api/checkout/status/${sessionId}`);

        // Server hands back a 5-min one-shot JWT when the buyer wasn't logged
        // in. Use it once to log them into their freshly-created account so
        // they land on the order detail page already authenticated.
        if (data.autoLoginToken && !autoLoginConsumed.current && !localStorage.getItem('iamhero_token')) {
          autoLoginConsumed.current = true;
          try { await applyToken(data.autoLoginToken); } catch {}
        }

        if (data.status === 'paid' || data.status === 'in_production') {
          setStatus('paid');
          setOrderId(data.orderId);
          setPayFirst(!!data.payFirst);
          // The just-paid order is no longer pending — drop it from the cart.
          refreshPendingOrders();
          // Also clear the guest cart entry (localStorage) saved at checkout so a
          // paid story can't linger with a "complete payment" link.
          if (data.shareToken) removeGuestItemByShareToken(data.shareToken);
          // Pay-first: the whole slots cart was just paid for — clear it so the
          // buyer isn't offered to pay for the same books again.
          if (data.payFirst) clearSlots();
          if (!purchaseTracked.current) {
            purchaseTracked.current = true;
            if (window.clarity) {
              if (data.orderId) window.clarity('set', 'orderId', String(data.orderId));
              window.clarity('event', 'purchase');
            }
            // eventID = orderId so Meta dedupes if the page is refreshed and
            // re-polls to a paid status.
            trackPurchase({
              orderId: data.orderId,
              value: data.totalCents != null ? data.totalCents / 100 : undefined,
              currency: data.currency,
            });
          }
          clearInterval(poll);
        } else if (data.status === 'cancelled') {
          setStatus('cancelled');
          clearInterval(poll);
        }
        attempts.current += 1;
        if (attempts.current > 30) {
          setStatus('pending');
          clearInterval(poll);
        }
      } catch {
        attempts.current += 1;
        if (attempts.current > 30) {
          setStatus('pending');
          clearInterval(poll);
        }
      }
    }, 2000);

    return () => clearInterval(poll);
  }, [sessionId, applyToken, refreshPendingOrders, removeGuestItemByShareToken, clearSlots]);

  if (status === 'polling') {
    return (
      <div className="checkout-loading">
        <div className="checkout-loading-card">
          <div className="checkout-loading-spinner" aria-hidden="true">
            <div className="checkout-loading-ring" />
            <div className="checkout-loading-pulse" />
          </div>
          <p className="checkout-loading-text">{t.paymentConfirming}</p>
          <p className="checkout-loading-hint">{t.paymentConfirmingHint || ''}</p>
        </div>
      </div>
    );
  }

  if (status === 'paid') {
    return (
      <div className="checkout-success">
        <div className="success-icon">&#10003;</div>
        <h2>{t.paymentSuccess}</h2>
        <p>{payFirst ? t.payFirstSuccessDesc : t.paymentSuccessDesc}</p>
        {orderId && (
          <Link to={`/account/orders/${orderId}`} className="submit-btn">
            {payFirst ? t.payFirstCreateNow : t.viewOrder}
          </Link>
        )}
      </div>
    );
  }

  if (status === 'pending') {
    return (
      <div className="checkout-success">
        <h2>{t.paymentPending}</h2>
      </div>
    );
  }

  return (
    <div className="checkout-success">
      <h2>{t.errorTitle}</h2>
    </div>
  );
}
